/* eslint-env node */

import {
	WebSocketGateway,
	CommentsService,
	TrackChangesService,
	RevisionHistoryService,
} from '@ckeditor/ckeditor-cloud-services-collaboration/dist/index.js'

import {
	Token
} from 'ckeditor5'

/**
 * Util, which firstly connects to CS, then saves users and comments to persistance storage.
 *
 * @param {String} documentId Document id.
 * @param {Array} changes
 * @param {Object} changes[n].user User data.
 * @param {Object} changes[n].comments List of comment threads.
 * @param {Object} changes[n].suggestions List of changes and suggestions.
 * @param {Object} config CS configuration.
 * @returns {Promise}
 */
export async function generateCommentsAndChanges(documentId, changes, config) {
	const wsGatewayPromises = [];
	const collaborationDataPromises = [];

	// Create WebSocket gateways for users from the initial data to make sure
	// that user objects are available in the document.
	for (const { user } of changes) {
		const wsGatewayPromise = createGateway(user, config);

		wsGatewayPromises.push(wsGatewayPromise);
	}

	const wsGateways = await Promise.all(wsGatewayPromises);
	const hasComments = await documentHasComments(wsGateways[0], documentId);

	if (hasComments) {
		return;
	}

	for (const entry of changes) {
		// Get the WebSocket gateway for the specific user to make sure that the suggestion data was saved
		// in the server with correct author ID.
		const wsGateway = wsGateways.find(gateway => gateway._socketAuth.userId === entry.user.id);

		if (entry.comments) {
			const comments = generateComments(wsGateway, documentId, entry.comments);

			collaborationDataPromises.push(comments);
		}

		if (entry.suggestions) {
			const suggestions = generateChanges(wsGateway, documentId, entry.suggestions);

			collaborationDataPromises.push(suggestions);
		}

		if (entry.revisions) {
			const revisions = generateRevisions(wsGateway, documentId, entry.revisions);

			collaborationDataPromises.push(revisions);
		}
	}

	return Promise.all(collaborationDataPromises);
}

/**
 * Util, which saves comments to persistance storage.
 *
 * @param {Object} wsGateway CS connection gateway.
 * @param {String} documentId Document id.
 * @param {Array} initialData List of comment threads.
 * @returns {Promise}
 */
function generateComments(wsGateway, documentId, initialData) {
	const service = new CommentsService(documentId);

	return service
		.connect(wsGateway)
		.then(() => {
			const promises = [];

			initialData.forEach((comment) => {
				promises.push(
					service.addComment(
						comment.thread,
						comment.commentId,
						comment.content
					)
				);
			});

			return Promise.all(promises);
		})
		.then(() => {
			service.disconnect();
		})
		.catch((err) => {
			service.disconnect();

			throw err;
		});
}

/**
 * Util, which saves changes and suggestions to persistance storage.
 *
 * @param {Object} wsGateway CS connection gateway.
 * @param {String} documentId Document id.
 * @param {Array} initialData List of Track Changes suggestions and updates.
 * @returns {Promise}
 */
function generateChanges(wsGateway, documentId, initialData) {
	const service = new TrackChangesService(documentId);

	return service
		.connect(wsGateway)
		.then(() => {
			const promises = [];

			initialData.forEach((change) => {
				promises.push(
					service.add(
						change.id,
						change.type,
						change.data,
						change.originalSuggestionId,
						change.attributes
					)
				);
			});

			return Promise.all(promises);
		})
		.then(() => {
			service.disconnect();
		})
		.catch((err) => {
			service.disconnect();

			throw err;
		});
}

/**
 * Util, which saves changes and suggestions to persistance storage.
 *
 * @param {Object} wsGateway CS connection gateway.
 * @param {String} documentId Document id.
 * @param {Array} initialData Array of revisions.
 * @returns {Promise}
 */
function generateRevisions(wsGateway, documentId, initialData) {
	const service = new RevisionHistoryService(documentId);

	return service
		.connect(wsGateway)
		.then((response) => {
			return service.updateRevisions(initialData, response.requestId);
		})
		.then(() => {
			service.disconnect();
		})
		.catch((err) => {
			service.disconnect();

			throw err;
		});
}

/**
 * Checks if the data for given document was already generated and sent to CS.
 *
 * @param {Object} wsGateway WebSocket Gateway instance.
 * @param {String} documentId Document id.
 * @returns {Promise}
 */
function documentHasComments(wsGateway, documentId) {
	const service = new CommentsService(documentId);

	return service.connect(wsGateway).then((response) => response.length > 0);
}

/**
 * @param {Object} user - Requested user data
 * @param {Object} config CS configuration.
 * @returns {Promise}
 */
function createGateway(user, config) {
	user = Object.assign({ id: uid() }, user);
	const tokenUrl = buildUserTokenUrl(config.tokenUrl, user);

	return Token.create(tokenUrl).then((token) =>
		WebSocketGateway.connect(token, config.webSocketUrl)
	);
}

/**
 * Create user token url, which is used in the Letters to CS connection.
 *
 * @param {String} baseUrl - Main part of the CS URL.
 * @param {Object} user - Requested user data
 * @returns {String} - Ready to use tokenUrl.
 */
function buildUserTokenUrl(baseUrl, user) {
	const url = baseUrl.indexOf('?') > -1 ? `${baseUrl}&` : `${baseUrl}?`;
	return (
		url +
		Object.keys(user)
			.filter((key) => user[key])
			.map((key) => `user.${key}=${user[key]}`)
			.join('&')
	);
}

/**
 * Create simple UID version.
 *
 * @param {String} hashLength - Length of the generated ID.
 * @returns {String} - Unique ID.
 */
function uid(hashLength = 32) {
	if (hashLength < 8) {
		hashLength = 8;
	}
	let uuid = 'e'; // Make sure that id does not start with number.
	const loopsQtty =
		hashLength % 4 ? Math.floor(hashLength / 4) + 1 : hashLength;

	for (let i = 0; i < loopsQtty; i++) {
		uuid += Math.floor((1 + Math.random()) * 0x10000)
			.toString(16)
			.substring(1);
	}

	return uuid.substr(0, hashLength);
}
